import {
  ArrowLeft,
  Check,
  FolderKanban,
  Loader2,
  LockKeyhole,
  Mail,
  UserRound,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Brand } from "../components/Brand";
import { safeReturnTo, validateReplacementPassword } from "../lib/auth";
import { friendlyAuthError, passwordFieldHint } from "../lib/authErrors";
import { appConfig } from "../lib/config";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { ensureInitialWorkspace } from "../lib/workspaces";

export function AuthPage() {
  const demoMode = appConfig.allowDemo && !isSupabaseConfigured;
  const [params, setParams] = useSearchParams();
  const requestedMode = params.get("mode");
  const initialMode =
    requestedMode === "signup"
      ? "signup"
      : requestedMode === "recovery"
        ? "recovery"
        : requestedMode === "forgot"
          ? "forgot"
          : "login";
  const [mode, setMode] = useState<"login" | "signup" | "forgot" | "recovery">(
    initialMode,
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnTo(
    params.get("returnTo") ||
      (location.state as { returnTo?: string } | null)?.returnTo,
  );
  const [recoveryValidated, setRecoveryValidated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendAfter, setResendAfter] = useState(0);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const invitationToken = params.get("invite") || "";
  const confirmationNotice = params.get("confirmed") === "1";
  const passwordChangedNotice = params.get("changed") === "1";

  useEffect(() => {
    const notice = confirmationNotice
      ? "Entre para continuar. A confirmação do endereço será verificada pelo serviço de autenticação."
      : passwordChangedNotice
        ? "Entre com sua senha para continuar."
        : "";
    if (!notice) return;

    setMessage(notice);
    const nextParams = new URLSearchParams(params);
    nextParams.delete("confirmed");
    nextParams.delete("changed");
    setParams(nextParams, { replace: true });
  }, [confirmationNotice, passwordChangedNotice, params, setParams]);

  useEffect(() => {
    if (!supabase) return;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setRecoveryValidated(true);
        setMode("recovery");
        setError("");
        setMessage("");
        setParams(
          (previous) => {
            const next = new URLSearchParams(previous);
            next.set("mode", "recovery");
            return next;
          },
          { replace: true },
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [setParams]);

  async function acceptInvitationIfPresent(): Promise<boolean> {
    if (!invitationToken || !supabase) return false;
    // Backend contract: accept_workspace_invitation(p_token).
    // The RPC hashes and consumes the token, validates its expiry and assigns
    // the authenticated user without trusting any workspace id from the URL.
    const { data: invitedWorkspaceId, error: invitationError } =
      await supabase.rpc("accept_workspace_invitation", {
        p_token: invitationToken,
      });
    if (invitationError) throw invitationError;
    const invitedWorkspace = Array.isArray(invitedWorkspaceId)
      ? invitedWorkspaceId[0]?.workspace_id
      : null;
    if (invitedWorkspace)
      sessionStorage.setItem("4apex.preferredWorkspace", invitedWorkspace);
    navigate("/app", {
      replace: true,
      state: { notice: "Convite aceito. Você já pode acessar o workspace." },
    });
    return true;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "");
    const password = String(form.get("password") || "");
    const passwordConfirmation = String(form.get("passwordConfirmation") || "");
    const fullName = String(form.get("fullName") || "");
    const workspaceName = String(form.get("workspaceName") || "");

    try {
      if (demoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 650));
        navigate(returnTo);
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        throw new Error(
          "A autenticação não está configurada neste ambiente. Defina VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY antes de publicar.",
        );
      }

      if (mode === "recovery") {
        if (!recoveryValidated)
          throw new Error(
            "Este link não foi validado ou expirou. Solicite um novo link.",
          );
        const validationError = validateReplacementPassword(
          password,
          passwordConfirmation,
        );
        if (validationError) throw new Error(validationError);

        const { error: updateError } = await supabase.auth.updateUser({
          password,
        });
        if (updateError) throw updateError;

        await supabase.auth.signOut({ scope: "local" });
        setMode("login");
        setParams({ returnTo }, { replace: true });
        setMessage(
          "Senha atualizada com segurança. Entre novamente para continuar.",
        );
        return;
      }

      if (mode === "forgot") {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          email,
          {
            redirectTo: `${window.location.origin}/auth?${new URLSearchParams({ mode: "recovery", returnTo, ...(invitationToken ? { invite: invitationToken } : {}) })}`,
          },
        );
        if (resetError) throw resetError;
        setMessage(
          "Se existir uma conta com este e-mail, enviaremos um link de recuperação. Verifique sua caixa de entrada e a de spam.",
        );
        return;
      }

      if (mode === "signup") {
        const confirmationParams = new URLSearchParams({
          mode: "login",
          confirmed: "1",
          returnTo,
        });
        if (invitationToken) {
          confirmationParams.set("invite", invitationToken);
        }
        let emailUsedTrial = false;
        if (isSupabaseConfigured && supabase) {
          const trialCheck = await supabase.rpc("check_email_trial_used", {
            p_email: email,
          });
          emailUsedTrial = trialCheck?.data === true;
        }
        const { data, error: signupError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              workspace_name: workspaceName,
              trial_days: appConfig.trialDays,
            },
            emailRedirectTo: `${
              window.location.origin
            }/auth?${confirmationParams.toString()}`,
          },
        });
        if (signupError) throw signupError;
        if (data.session) {
          if (await acceptInvitationIfPresent()) return;
          await ensureInitialWorkspace(workspaceName);
          if (emailUsedTrial) {
            navigate("/app/billing?trial=used");
          } else {
            navigate("/app/projects/new");
          }
        } else {
          setPendingEmail(email);
          setResendAfter(Date.now() + 60_000);
        }
        return;
      }

      const { data: loginData, error: loginError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });
      if (loginError) throw loginError;
      if (await acceptInvitationIfPresent()) return;
      const metadata = loginData.user.user_metadata;
      await ensureInitialWorkspace(
        String(
          metadata.workspace_name ||
            metadata.full_name ||
            `${email.split("@")[0]} workspace`,
        ),
      );
      navigate(returnTo);
    } catch (caught) {
      setError(friendlyAuthError(caught));
    } finally {
      setLoading(false);
    }
  }

  function changeMode(next: "login" | "signup" | "forgot") {
    setMode(next);
    setError("");
    setMessage("");
    setPendingEmail("");
    setPasswordValue("");
    const nextParams = new URLSearchParams({ returnTo });
    nextParams.set("mode", next);
    if (invitationToken) nextParams.set("invite", invitationToken);
    setParams(nextParams);
  }

  return (
    <div className="auth-page">
      <section className="auth-page__brand">
        <Brand inverse />
        <div>
          <h2>O conhecimento do seu produto, disponível em cada tela.</h2>
          <p>
            Configure um assistente por projeto, instale com segurança no APEX e
            aprenda com cada conversa.
          </p>
          <ul>
            <li>
              <Check /> Base de conhecimento isolada
            </li>
            <li>
              <Check /> Widget elegante e acessível
            </li>
            <li>
              <Check /> Trial configurável, sem cartão agora
            </li>
          </ul>
        </div>
        <span>
          {appConfig.trialDays} dias grátis • {appConfig.name}
        </span>
      </section>

      <main className="auth-page__form">
        <Link className="auth-back" to="/">
          <ArrowLeft size={16} /> Voltar ao site
        </Link>
        <div className="auth-card">
          <h1>
            {mode === "signup"
              ? invitationToken
                ? "Crie sua conta"
                : "Crie seu workspace"
              : mode === "forgot"
                ? "Recupere seu acesso"
                : mode === "recovery"
                  ? "Defina uma nova senha"
                  : "Entre no 4apex"}
          </h1>
          <p>
            {mode === "signup"
              ? invitationToken
                ? "Use o e-mail convidado para entrar no workspace."
                : `Comece gratuitamente por ${appConfig.trialDays} dias.`
              : mode === "forgot"
                ? "Informe seu e-mail para receber um link seguro."
                : mode === "recovery"
                  ? recoveryValidated
                    ? "Link validado. Escolha uma nova senha."
                    : "Este link ainda não foi validado ou expirou. Solicite um novo link de recuperação."
                  : "Acesse seus projetos, conversas e insights."}
          </p>

          {demoMode ? (
            <div className="demo-notice">
              Ambiente de demonstração: qualquer e-mail abre o painel sem criar
              uma conta real.
            </div>
          ) : !isSupabaseConfigured ? (
            <div className="demo-notice" role="alert">
              Autenticação indisponível: este build não recebeu a configuração
              pública do Supabase.
            </div>
          ) : null}
          {invitationToken ? (
            <div className="form-message" role="status">
              Entre ou crie sua conta com o e-mail convidado. O workspace é
              associado automaticamente ao aceitar o convite.
            </div>
          ) : null}

          {pendingEmail ? (
            <div className="auth-confirmation">
              <span className="auth-confirmation__icon">
                <Mail size={26} />
              </span>
              <h3>Confira seu e-mail para continuar.</h3>
              <p>
                Enviamos uma solicitação de confirmação para{" "}
                <strong>{pendingEmail}</strong>. Se o e-mail tiver sido enviado,
                abra o link que ele contém para liberar o acesso ao painel.
              </p>
              <ol>
                <li>
                  <span>1</span> Abra sua caixa de entrada
                </li>
                <li>
                  <span>2</span> Clique no link "Confirme seu e-mail"
                </li>
                <li>
                  <span>3</span> Volte aqui e faça login
                </li>
              </ol>
              <p className="auth-confirmation__note">
                Não recebeu? Verifique a caixa de entrada e o spam. Você também
                pode corrigir o endereço e solicitar outro link.
              </p>
              {error ? <p role="alert">{error}</p> : null}
              {message ? <p role="status">{message}</p> : null}
              <button
                className="button button--secondary"
                type="button"
                disabled={loading || clock < resendAfter}
                onClick={async () => {
                  if (!supabase || clock < resendAfter) return;
                  setLoading(true);
                  setResendAfter(Date.now() + 60_000);
                  setError("");
                  try {
                    const redirect = new URLSearchParams({
                      confirmed: "1",
                      returnTo,
                      ...(invitationToken ? { invite: invitationToken } : {}),
                    });
                    const { error: resendError } = await supabase.auth.resend({
                      type: "signup",
                      email: pendingEmail,
                      options: {
                        emailRedirectTo: `${window.location.origin}/auth?${redirect}`,
                      },
                    });
                    if (resendError) throw resendError;
                    setMessage(
                      "Se o cadastro puder ser concluído com este endereço, você receberá um link.",
                    );
                  } catch (caught) {
                    setError(friendlyAuthError(caught));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {clock < resendAfter
                  ? `Aguarde ${Math.ceil((resendAfter - clock) / 1000)}s`
                  : "Reenviar confirmação"}
              </button>
              <button
                className="auth-link"
                type="button"
                onClick={() => changeMode("signup")}
              >
                Corrigir endereço
              </button>
              <button
                className="button button--primary button--large"
                type="button"
                onClick={() => {
                  setPendingEmail("");
                  setMode("login");
                  setParams(
                    invitationToken
                      ? { mode: "login", invite: invitationToken, returnTo }
                      : { mode: "login", returnTo },
                    { replace: true },
                  );
                  setMessage(
                    "Confirme seu e-mail antes de entrar. Se não recebeu o link, verifique a caixa de spam.",
                  );
                }}
              >
                Voltar para o login
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={submit}>
                {mode !== "forgot" ? (
                  <button
                    type="button"
                    className="auth-link"
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? "Ocultar senha" : "Mostrar senha"}
                  </button>
                ) : null}
                {mode === "recovery" && !recoveryValidated ? (
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => changeMode("forgot")}
                  >
                    Solicitar novo link
                  </button>
                ) : null}
                {mode === "signup" ? (
                  <>
                    <label>
                      Nome completo
                      <span>
                        <UserRound size={17} />
                        <input
                          name="fullName"
                          required
                          autoComplete="name"
                          placeholder="Seu nome"
                        />
                      </span>
                    </label>
                    {invitationToken ? null : (
                      <label>
                        Nome do workspace
                        <span>
                          <FolderKanban size={17} />
                          <input
                            name="workspaceName"
                            required
                            minLength={2}
                            maxLength={120}
                            placeholder="Ex.: Simplifica Gestão"
                          />
                        </span>
                      </label>
                    )}
                  </>
                ) : null}
                {mode === "recovery" ? null : (
                  <label>
                    E-mail
                    <span>
                      <Mail size={17} />
                      <input
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="voce@empresa.com.br"
                      />
                    </span>
                  </label>
                )}
                {mode === "forgot" ? null : (
                  <label>
                    {mode === "recovery" ? "Nova senha" : "Senha"}
                    <span>
                      <LockKeyhole size={17} />
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        minLength={8}
                        required
                        autoComplete={
                          mode === "login" ? "current-password" : "new-password"
                        }
                        placeholder="Mínimo de 8 caracteres"
                        aria-describedby={
                          mode === "login" ? undefined : "password-hint"
                        }
                        onChange={(event) =>
                          setPasswordValue(event.target.value)
                        }
                      />
                    </span>
                    {mode !== "login" ? (
                      <small
                        id="password-hint"
                        className={
                          passwordValue.length > 0 && passwordValue.length < 8
                            ? "auth-hint auth-hint--warn"
                            : "auth-hint"
                        }
                      >
                        {passwordFieldHint(passwordValue, mode)}
                      </small>
                    ) : null}
                  </label>
                )}
                {mode === "recovery" ? (
                  <label>
                    Confirme a nova senha
                    <span>
                      <LockKeyhole size={17} />
                      <input
                        name="passwordConfirmation"
                        type={showPassword ? "text" : "password"}
                        minLength={8}
                        required
                        autoComplete="new-password"
                        placeholder="Repita a nova senha"
                        aria-describedby="password-confirmation-hint"
                      />
                    </span>
                    <small
                      id="password-confirmation-hint"
                      className="auth-hint"
                    >
                      Repita a nova senha para confirmar.
                    </small>
                  </label>
                ) : null}
                {mode === "signup" ? (
                  <label className="auth-terms">
                    <input type="checkbox" required />
                    <span>
                      Li e aceito os <Link to="/terms">Termos</Link> e a{" "}
                      <Link to="/privacy">Política de Privacidade</Link>.
                    </span>
                  </label>
                ) : null}
                {error ? (
                  <div
                    className="form-message form-message--error"
                    role="alert"
                    aria-live="assertive"
                  >
                    {error}
                  </div>
                ) : null}
                {message ? (
                  <div
                    className="form-message form-message--success"
                    role="status"
                    aria-live="polite"
                  >
                    {message}
                  </div>
                ) : null}
                <button
                  className="button button--primary button--large"
                  type="submit"
                  disabled={
                    loading || (mode === "recovery" && !recoveryValidated)
                  }
                >
                  {loading ? <Loader2 className="spin" size={18} /> : null}
                  {mode === "signup"
                    ? "Criar conta grátis"
                    : mode === "forgot"
                      ? "Enviar link"
                      : mode === "recovery"
                        ? "Atualizar senha"
                        : "Entrar"}
                </button>
              </form>

              {mode === "login" ? (
                <button
                  className="auth-link"
                  type="button"
                  onClick={() => changeMode("forgot")}
                >
                  Esqueci minha senha
                </button>
              ) : null}
              {mode === "recovery" ? null : (
                <div className="auth-switch">
                  {mode === "signup" ? (
                    <>
                      Já tem conta?{" "}
                      <button type="button" onClick={() => changeMode("login")}>
                        Entrar
                      </button>
                    </>
                  ) : (
                    <>
                      Ainda não tem conta?{" "}
                      <button
                        type="button"
                        onClick={() => changeMode("signup")}
                      >
                        Criar grátis
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
